import { Input } from '@workspace/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
import { cn } from '@workspace/ui/lib/utils';
import { useEffect, useState } from 'react';

const COUNTRY_CODES = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸' },
  { code: '+52', country: 'MX', flag: '🇲🇽' },
  { code: '+57', country: 'CO', flag: '🇨🇴' },
  { code: '+593', country: 'EC', flag: '🇪🇨' },
  { code: '+380', country: 'UA', flag: '🇺🇦' },
  { code: '+54', country: 'AR', flag: '🇦🇷' },
  { code: '+56', country: 'CL', flag: '🇨🇱' },
  { code: '+51', country: 'PE', flag: '🇵🇪' },
  { code: '+58', country: 'VE', flag: '🇻🇪' },
  { code: '+34', country: 'ES', flag: '🇪🇸' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+39', country: 'IT', flag: '🇮🇹' },
  { code: '+55', country: 'BR', flag: '🇧🇷' },
];

export function PhoneInput({
  placeholder,
  required,
  value,
  onChange,
  hasError,
}: {
  name: string;
  placeholder: string;
  required: boolean;
  value: string;
  onChange: (value: string) => void;
  hasError: boolean;
}) {
  const [countryCode, setCountryCode] = useState(() => {
    if (value && typeof value === 'string') {
      const matchedCode = COUNTRY_CODES.find((cc) =>
        value.startsWith(cc.code.replace('+', '')),
      );
      return matchedCode ? matchedCode.code : '+57';
    }
    return '+57';
  });

  const [phoneNumber, setPhoneNumber] = useState(() => {
    if (value && typeof value === 'string' && countryCode) {
      const codeWithoutPlus = countryCode.replace('+', '');
      return value.startsWith(codeWithoutPlus)
        ? value.substring(codeWithoutPlus.length)
        : value;
    }
    return '';
  });

  useEffect(() => {
    if (value && typeof value === 'string' && value.length > 0) {
      const matchedCode = COUNTRY_CODES.find((cc) =>
        value.startsWith(cc.code.replace('+', '')),
      );
      const newCountryCode = matchedCode ? matchedCode.code : '+57';
      const codeWithoutPlus = newCountryCode.replace('+', '');
      const newPhoneNumber = value.startsWith(codeWithoutPlus)
        ? value.substring(codeWithoutPlus.length)
        : value;

      setCountryCode(newCountryCode);
      setPhoneNumber(newPhoneNumber || '');
    } else {
      // Empty value, keep default country code but clear phone number
      setPhoneNumber('');
    }
  }, [value]);

  const handleCountryCodeChange = (newCode: string) => {
    setCountryCode(newCode);
    const codeWithoutPlus = newCode.replace('+', '');
    const currentPhone = phoneNumber || '';
    const fullNumber = currentPhone
      ? `${codeWithoutPlus}${currentPhone}`
      : codeWithoutPlus;
    onChange(fullNumber);
  };

  const handlePhoneNumberChange = (newNumber: string) => {
    const cleanNumber = newNumber.replace(/\D/g, '');
    setPhoneNumber(cleanNumber);
    const codeWithoutPlus = countryCode.replace('+', '');
    const fullNumber = cleanNumber
      ? `${codeWithoutPlus}${cleanNumber}`
      : codeWithoutPlus;
    onChange(fullNumber);
  };

  return (
    <div className="flex gap-2">
      <Select value={countryCode} onValueChange={handleCountryCodeChange}>
        <SelectTrigger
          tabIndex={-1}
          className={cn(
            'dark:bg-background border-[#27272a] text-white text-sm h-8',
            {
              'border-red-500': hasError,
            },
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {COUNTRY_CODES.map((country) => (
            <SelectItem key={country.code} value={country.code}>
              <span className="flex items-center gap-2">
                <span>{country.flag}</span>
                <span>{country.code}</span>
                <span className="text-xs text-muted-foreground">
                  {country.country}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        id="phone-number-input"
        type="tel"
        placeholder={placeholder}
        value={phoneNumber}
        onInput={(e) => handlePhoneNumberChange(e.currentTarget.value)}
        className={`flex-1 ${hasError ? 'border-red-500' : ''}`}
        required={required}
      />
    </div>
  );
}
